import { useMemo, useState } from 'react'
import { Link, useNavigate } from 'react-router-dom'
import { Plus } from 'lucide-react'
import { Badge, Can, EmptyState, PageHeader, SearchInput } from '@/kit/ui'
import { PARTNER_TYPE_LABEL, SURFACE_LABEL, lenderLine, statusLabel } from '@/lib/lender'
import { useHubStore } from '@/store/hubStore'
import { ActiveSwitch, LenderLogo } from './LenderBits'

/** Lenders (F1-2, F1-3): the table with the one Active switch; a row opens the lender. */
export function LendersScreen() {
  const lenders = useHubStore((s) => s.lenders)
  const navigate = useNavigate()
  const [q, setQ] = useState('')
  const rows = useMemo(() => {
    const t = q.trim().toLowerCase()
    return lenders.filter((l) => !t || `${l.name} ${l.slug} ${l.legalName} ${l.lenders.map((r) => r.legalName).join(' ')}`.toLowerCase().includes(t))
  }, [lenders, q])
  const active = lenders.filter((l) => l.status === 'active').length

  return (
    <>
      <PageHeader
        title="Lenders"
        subtitle={`${lenders.length} lenders · ${active} live · the one place a lender is switched on or off`}
        actions={
          <>
            <SearchInput value={q} onChange={setQ} placeholder="Search lender…" className="w-56" />
            <Can permission="lenders.edit">
              <Link to="/lenders/new" className="btn btn-primary no-underline"><Plus size={12} /> Add a lender</Link>
            </Can>
          </>
        }
      />
      <div className="mb-3 rounded border-l-[3px] border-primary bg-primary-soft px-3 py-2 text-xs text-primary-text">
        <b>One switch, everywhere.</b> Off here = endpoints suspended at once, out of the app within 5 minutes. Nothing is deleted. <b>No leads are stored in this portal.</b>
      </div>
      {rows.length === 0 ? (
        <EmptyState title="No lender matches" description="Try another name or slug." />
      ) : (
        <div className="card-flush overflow-x-auto">
          <table className="table">
            <thead>
              <tr>
                <th>Lender</th><th>Type</th><th>Products</th><th>Active</th><th>Used by</th><th>API batch (live)</th><th>Workflows → endpoints</th><th></th>
              </tr>
            </thead>
            <tbody>
              {rows.map((l) => (
                <tr key={l.slug} className="row-link" onClick={() => navigate(`/lenders/${l.slug}`)}>
                  <td>
                    <div className="flex items-center gap-2">
                      <LenderLogo lender={l} size={28} />
                      <div>
                        <Link to={`/lenders/${l.slug}`} className="font-semibold text-primary no-underline hover:underline" onClick={(e) => e.stopPropagation()}>{l.name}</Link>
                        <div className="text-2xs text-muted"><span className="mono">{l.slug}</span> · {lenderLine(l)}</div>
                      </div>
                    </div>
                  </td>
                  <td>
                    <div>{PARTNER_TYPE_LABEL[l.partnerType]}</div>
                    <Badge tone={l.status === 'off' ? 'danger' : l.status === 'draft' ? 'gray' : 'success'}>{statusLabel(l)}</Badge>
                  </td>
                  <td>{l.products.map((p) => p.name).join(' · ') || '—'}</td>
                  <td onClick={(e) => e.stopPropagation()}><ActiveSwitch lender={l} /></td>
                  <td>
                    <div className="flex flex-wrap gap-1">
                      {l.status === 'off' ? <Badge tone="danger">hidden everywhere</Badge> : l.usedBy.length ? l.usedBy.map((s) => <Badge key={s}>{SURFACE_LABEL[s]}</Badge>) : <span className="text-muted">—</span>}
                    </div>
                  </td>
                  <td>
                    <div className="flex flex-wrap gap-1">
                      {l.liveBatch ? <Badge tone="success">batchApi {l.liveBatch}</Badge> : null}
                      {l.draftBatch ? <Badge tone="warning">{l.draftBatch} draft</Badge> : null}
                      {!l.liveBatch && !l.draftBatch ? <Badge>none</Badge> : null}
                    </div>
                  </td>
                  <td>
                    <div className="flex flex-wrap gap-1">
                      {l.workflows.filter((w) => w.state !== 'previous').map((w) => (
                        <Badge key={w.name + w.version} tone={w.state === 'live' ? 'success' : w.state === 'suspended' ? 'warning' : 'gray'}>
                          {w.name.replace(`${l.slug}.`, '')} {w.version} → {w.endpointId.replace(`${l.slug}.`, '')}{w.state === 'suspended' ? ' (suspended)' : w.state === 'draft' ? ' (draft)' : ''}
                        </Badge>
                      ))}
                      {l.workflows.length === 0 && <span className="text-muted">—</span>}
                    </div>
                  </td>
                  <td className="text-right"><Link to={`/lenders/${l.slug}`} className="btn btn-secondary btn-sm no-underline" onClick={(e) => e.stopPropagation()}>Open</Link></td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
      <p className="mt-2 text-2xs text-muted">
        Names: <code>&lt;lender&gt;.batchApi.&lt;version&gt;</code> (API code) · <code>&lt;lender&gt;.&lt;product&gt;.flow &lt;version&gt;</code> (workflow) · <code>&lt;lender&gt;.&lt;product&gt;</code> (endpoint).
      </p>
    </>
  )
}
